import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import './BotRegisteredManagementTable.scss';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit3,
  Activity,
  Pause,
  DollarSign,
  TrendingUp,
  BarChart3,
  Filter,
  Bot
} from 'lucide-react';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  }
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const columns = [
  { id: 'index', label: '#', sortable: false },
  { id: 'exchange', label: 'Exchange', sortable: true },
  { id: 'bot_name', label: 'Bot Name', sortable: true },
  { id: 'investment', label: 'Investment', sortable: true },
  { id: 'portion', label: 'Lot', sortable: true },
  { id: 'risk', label: 'Risk', sortable: true },
  { id: 'registration_status', label: 'Status', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false }
];

function BotRegisteredManagementTable(props) {
  const { registeredBotData } = props;
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const formatValue = (value) => {
    return value?.toLocaleString('tr-TR') || '0';
  };

  useEffect(() => {
    if (registeredBotData) {
      const data = Object.values(registeredBotData || {});
      setTotalRows(data.length);
      setTableData(data);
    }
  }, [registeredBotData]);

  function editBot(item) {
    history.push({
      pathname: `/general-settings/update-bot-management/${item.bot_id}`,
      state: {
        item: item,
      },
    });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    scrollToBotHistoryContainer();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    scrollToBotHistoryContainer();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function scrollToBotHistoryContainer() {
    const botHistory = document.getElementById('bot-history-container');
    if (botHistory) {
      botHistory.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const getSortedData = () => {
    const stabilizedData = stableSort(tableData, getComparator(order, orderBy));
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return stabilizedData.slice(startIndex, endIndex);
  };

  // Modern Status Chip Component
  const ModernStatusChip = ({ status }) => (
    <Chip
      icon={status ? <Activity size={14} /> : <Pause size={14} />}
      label={status ? "Active" : "Inactive"}
      className={`modern-status-chip ${status ? 'active' : 'inactive'}`}
      size="small"
      variant="filled"
    />
  );

  // Modern Exchange Badge Component
  const ExchangeBadge = ({ exchange }) => (
    <div className="exchange-badge">
      <span className="exchange-name">
        {exchange.charAt(0).toUpperCase() + exchange.slice(1).toLowerCase()}
      </span>
    </div>
  );

  console.log('registeredBotData')
  console.log(registeredBotData)

  return (
    <div className="modern-bot-registered-table">
      <Paper elevation={0} className="modern-table-paper">
        {/* Table Controls */}
        {tableData.length > 0 && (
          <Box className="modern-table-controls">
            <div className="controls-left">
              <Typography variant="body2" component="span" className="control-label">
                <Filter size={16} className="control-icon" />
                Show:
              </Typography>
              <FormControl variant="standard" size="small" className="modern-limit-select">
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  displayEmpty
                  className="modern-select"
                >
                  {[5, 10, 25].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option} rows
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="controls-right">
              <Typography variant="body2" component="span" className="count-display">
                <BarChart3 size={16} className="control-icon" />
                Total: {totalRows} {totalRows === 1 ? 'Bot' : 'Bots'}
              </Typography>
            </div>
          </Box>
        )}

        <TableContainer className="modern-table-container" id="bot-history-container">
          <Table stickyHeader aria-label="registered bots table" className="modern-table">
            <TableHead>
              <TableRow className="modern-header-row">
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className="modern-header-cell"
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                        className="modern-sort-label"
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <TableRow
                    key={index}
                    hover
                    className="modern-table-row"
                  >
                    <TableCell className="modern-cell index-cell">
                      <div className="row-index">
                        {page * rowsPerPage + index + 1}
                      </div>
                    </TableCell>

                    <TableCell className="modern-cell exchange-cell">
                      <ExchangeBadge exchange={item.exchange} />
                    </TableCell>

                    <TableCell className="modern-cell bot-name-cell">
                      <div className="bot-name-container">
                        <Bot size={16} className="bot-icon" />
                        <span className="bot-name">{item.bot_name}</span>
                      </div>
                    </TableCell>

                    <TableCell className="modern-cell investment-cell">
                      <div className="investment-container">
                        <DollarSign size={16} className="dollar-icon" />
                        <span className="investment-amount">
                          {formatValue(item.investment)}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell className="modern-cell portion-cell">
                      <div className="portion-container">
                        <TrendingUp size={16} className="portion-icon" />
                        <span className="portion-value">{item.portion}</span>
                      </div>
                    </TableCell>

                    <TableCell className="modern-cell risk-cell">
                      <div className={`risk-container ${item.risk?.toLowerCase()}`}>
                        <BarChart3 size={16} className="risk-icon" />
                        <span className="risk-label">
                          {item.risk?.charAt(0).toUpperCase() + item.risk?.slice(1).toLowerCase()}
                        </span>
                      </div>
                    </TableCell>


                    <TableCell className="modern-cell status-cell">
                      <ModernStatusChip status={item.registration_status} />
                    </TableCell>


                    <TableCell className="modern-cell actions-cell">
                      <button
                        className="modern-action-btn edit-btn"
                        onClick={() => editBot(item)}
                        title="Edit Bot Settings"
                      >
                        <Edit3 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="modern-empty-message">
                    <div className="empty-state">
                      <Bot size={48} className="empty-icon" />
                      <Typography variant="h6">No Registered Bots Found</Typography>
                      <Typography variant="body2" color="textSecondary">
                        You haven't registered any trading bots yet
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modern Pagination */}
        {totalRows > 0 && (
          <div className="modern-pagination-container">
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              className="modern-pagination"
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </div>
        )}
      </Paper>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    registeredBotData: state.botReducer.registeredBotData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(BotRegisteredManagementTable);